import React, { createContext, useEffect, useMemo, useState } from "react";
import productData from "../Datas/productData";

export const CartContext = createContext();

const productsById = new Map(productData.map((product) => [product.id, product]));

const readStoredValue = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const storedValue = window.localStorage.getItem(key);
    if (!storedValue) return fallback;
    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? parsedValue : fallback;
  } catch {
    return fallback;
  }
};

const normalizeCartItems = (items) => {
  if (!Array.isArray(items)) return [];

  return items.reduce((normalizedItems, item) => {
    const id = Number(item?.id);
    const product = productsById.get(id);
    const stock = Math.max(0, Math.floor(Number(product?.stock) || 0));
    if (!product || stock === 0) return normalizedItems;

    const size = typeof item.size === "string" ? item.size : "";
    const color = typeof item.color === "string" ? item.color : "";
    const quantity = Math.min(
      stock,
      Math.max(1, Math.floor(Number(item.quantity) || 1))
    );
    const existingItem = normalizedItems.find(
      (candidate) =>
        candidate.id === id && candidate.size === size && candidate.color === color
    );

    if (existingItem) {
      existingItem.quantity = Math.min(stock, existingItem.quantity + quantity);
      return normalizedItems;
    }

    normalizedItems.push({ id, size, color, quantity });
    return normalizedItems;
  }, []);
};

const normalizeWishlistItems = (items) => {
  if (!Array.isArray(items)) return [];
  return [
    ...new Set(
      items
        .map((id) => Number(id))
        .filter((id) => productsById.has(id))
    ),
  ];
};

const writeStoredValue = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Shopping remains available when storage is disabled or full.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normalizeCartItems(readStoredValue("cartItems", []))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    normalizeWishlistItems(readStoredValue("wishlistItems", []))
  );

  useEffect(() => {
    writeStoredValue("cartItems", cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStoredValue("wishlistItems", wishlistItems);
  }, [wishlistItems]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    const id = Number(productId);
    const product = productsById.get(id);
    const stock = Math.max(0, Math.floor(Number(product?.stock) || 0));
    if (!product || stock === 0) return;

    const amount = Math.min(
      stock,
      Math.max(1, Math.floor(Number(quantity) || 1))
    );
    const normalizedSize = typeof size === "string" ? size : "";
    const normalizedColor = typeof color === "string" ? color : "";

    setCartItems((currentItems) => {
      const existingItem = currentItems.find(
        (item) =>
          item.id === id &&
          item.size === normalizedSize &&
          item.color === normalizedColor
      );

      if (existingItem) {
        return currentItems.map((item) =>
          item.id === id &&
          item.size === normalizedSize &&
          item.color === normalizedColor
            ? {
                ...item,
                quantity: Math.min(stock, item.quantity + amount),
              }
            : item
        );
      }

      return [
        ...currentItems,
        {
          id,
          size: normalizedSize,
          color: normalizedColor,
          quantity: amount,
        },
      ];
    });
  };

  const removeFromCart = (productId, size, color) => {
    const id = Number(productId);
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(item.id === id && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const id = Number(productId);
    const stock = Math.max(
      1,
      Math.floor(Number(productsById.get(id)?.stock) || 1)
    );
    const amount = Math.min(
      stock,
      Math.max(1, Math.floor(Number(quantity) || 1))
    );
    setCartItems((currentItems) =>
      currentItems.map((item) =>
        item.id === id && item.size === size && item.color === color
          ? { ...item, quantity: amount }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const addToWishlist = (productId) => {
    const id = Number(productId);
    if (!productsById.has(id)) return;
    setWishlistItems((currentItems) =>
      currentItems.includes(id)
        ? currentItems
        : [...currentItems, id]
    );
  };

  const removeFromWishlist = (productId) => {
    const id = Number(productId);
    setWishlistItems((currentItems) =>
      currentItems.filter((itemId) => itemId !== id)
    );
  };

  const toggleWishlist = (productId) => {
    const id = Number(productId);
    if (!productsById.has(id)) return;
    setWishlistItems((currentItems) =>
      currentItems.includes(id)
        ? currentItems.filter((itemId) => itemId !== id)
        : [...currentItems, id]
    );
  };

  const clearWishlist = () => setWishlistItems([]);
  const isWishlisted = (productId) => wishlistItems.includes(Number(productId));
  const cartCount = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0
      ),
    [cartItems]
  );

  const value = {
    cartItems,
    cartCount,
    addToCart,
    removeFromCart,
    clearCart,
    updateQuantity,
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    clearWishlist,
    isWishlisted,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
